import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Tabs, type TabDef } from '../components/Tabs.js';
import { CustomerWizard } from '../components/CustomerWizard.js';

// Locker sizes a lead can be interested in — value = the locker system's own
// size code, label = the plain M/L/XL the operator asked for.
const LOCKER_SIZES = [
  { value: 'Medium', label: 'Medium (M)' },
  { value: 'L', label: 'Large (L)' },
  { value: 'XL', label: 'Extra Large (XL)' },
];

interface Lead {
  id: number;
  full_name: string;
  phone: string | null;
  place: string | null;
  district: string | null;
  category: string | null;
  source: string | null;
  referred_by_text: string | null;
  lead_type: string | null;
  interested_scheme: string | null;
  locker_size: string | null;
  status: string;
  expected_amount: string | null;
  follow_up_date: string | null;
  notes: string | null;
}

interface LeadNote {
  id: number;
  note: string;
  created_at: string;
  author: string | null;
}

interface Prospect {
  id: number;
  customer_code: string;
  full_name: string;
  phone: string | null;
  district: string | null;
  kyc_status: string | null;
  created_at: string;
}

const PROSPECTS_TAB = '__prospects__';

const EMPTY_FORM = {
  full_name: '', phone: '', place: '', district: '', category: '', source: '',
  referred_by_text: '', lead_type: 'ncd', interested_scheme: '', locker_size: '',
  expected_amount: '', follow_up_date: '', status: '', notes: '',
};

const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';

/** Labelled form field for the lead modal. */
function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-label">{label}{required && <span className="text-danger"> *</span>}</span>
      {children}
    </label>
  );
}

/** Follow-up notes for one lead: history + add box. */
function NotesPanel({ leadId, canUpdate }: { leadId: number; canUpdate: boolean }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['lead-notes', leadId],
    queryFn: () => api.get<{ rows: LeadNote[] }>(`/api/leads/${leadId}/notes`),
  });
  const add = useMutation({
    mutationFn: () => api.post(`/api/leads/${leadId}/notes`, { note: note.trim() }),
    onSuccess: () => { setNote(''); qc.invalidateQueries({ queryKey: ['lead-notes', leadId] }); },
  });
  return (
    <div className="bg-bg rounded p-3 mx-4 mb-3 text-xs">
      {canUpdate && (
        <div className="flex gap-2 mb-2">
          <input className={`${inp} flex-1 text-xs`} placeholder="Add a follow-up note…" value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) add.mutate(); }} autoFocus />
          <button disabled={!note.trim() || add.isPending} onClick={() => add.mutate()}
            className="text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40 hover:bg-primary-hover">Add</button>
        </div>
      )}
      {isLoading ? 'Loading…' : (data?.rows ?? []).length === 0 ? <span className="text-text-muted">No notes yet.</span> : (
        <ul className="m-0 p-0 list-none space-y-1.5">
          {data!.rows.map((n) => (
            <li key={n.id}>
              <span className="text-text-muted font-mono">{String(n.created_at).slice(0, 10)}</span>
              {n.author && <span className="text-text-muted"> · {n.author}</span>} — {n.note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function LeadsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState('');
  const [notesFor, setNotesFor] = useState<number | null>(null);
  /** The lead open in the full edit form (owner 2026-09-21: "i need the full
   *  editing thing where i can edit everything about the lead"). It replaces
   *  the old inline edit, which offered only status, follow-up and amount. */
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [tab, setTab] = useState<string>('all');
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState('');

  const { data, isLoading, error } = useQuery({ queryKey: ['leads'], queryFn: () => api.get<{ rows: Lead[] }>('/api/leads') });
  const prospects = useQuery({ queryKey: ['app-prospects'], queryFn: () => api.get<{ rows: Prospect[] }>('/api/leads/app-prospects') });

  // Configurable vocabularies (docs/07 — no hardcoded business values).
  const uiConfig = useQuery({
    queryKey: ['ui-config'],
    queryFn: () => api.get<{ values: Record<string, string[] | null> }>('/api/settings/ui-config'),
  });
  const SOURCES = uiConfig.data?.values['customers.lead_sources'] ?? [];
  const STATUSES = uiConfig.data?.values['customers.lead_statuses'] ?? [];
  const CATEGORIES = uiConfig.data?.values['customers.lead_categories'] ?? [];
  const REFERRED_BY = uiConfig.data?.values['customers.lead_referred_by'] ?? [];
  const SCHEMES = uiConfig.data?.values['customers.lead_interested_schemes'] ?? [];

  // Duplicate-phone check while typing a new lead's phone.
  const phone = form.phone.trim();
  const dup = useQuery({
    queryKey: ['lead-dup', phone],
    queryFn: () => api.get<{ duplicate: boolean; customer?: { id: number; customer_code: string; full_name: string } }>(`/api/leads/duplicate-check?phone=${encodeURIComponent(phone)}`),
    enabled: phone.length >= 10,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/leads', {
      full_name: form.full_name,
      ...(form.phone ? { phone: form.phone } : {}),
      ...(form.place ? { place: form.place } : {}),
      ...(form.district ? { district: form.district } : {}),
      ...(form.category ? { category: form.category } : {}),
      ...(form.source ? { source: form.source } : {}),
      ...(form.referred_by_text ? { referred_by_text: form.referred_by_text } : {}),
      lead_type: form.lead_type,
      // Only the field that matches the type — the server nulls the other anyway.
      ...(form.lead_type === 'ncd' && form.interested_scheme ? { interested_scheme: form.interested_scheme } : {}),
      ...(form.lead_type === 'locker' && form.locker_size ? { locker_size: form.locker_size } : {}),
      ...(form.expected_amount ? { expected_amount: Number(form.expected_amount) } : {}),
      ...(form.follow_up_date ? { follow_up_date: form.follow_up_date } : {}),
      ...(form.status ? { status: form.status } : {}),
      ...(form.notes ? { notes: form.notes } : {}),
    }),
    onSuccess: () => { setForm(EMPTY_FORM); setCreating(false); qc.invalidateQueries({ queryKey: ['leads'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed'),
  });

  // Every field goes, null where a box was emptied — so a phone, a date or an
  // amount can be taken OFF a lead, not only changed. The server clears the
  // scheme/size that no longer applies when the type changes.
  const update = useMutation({
    mutationFn: (id: number) => {
      const v = (x: string) => (x.trim() ? x.trim() : null);
      return api.put(`/api/leads/${id}`, {
        full_name: form.full_name.trim(),
        phone: v(form.phone), place: v(form.place), district: v(form.district),
        category: v(form.category), source: v(form.source), referred_by_text: v(form.referred_by_text),
        lead_type: form.lead_type,
        interested_scheme: form.lead_type === 'ncd' ? v(form.interested_scheme) : null,
        locker_size: form.lead_type === 'locker' ? v(form.locker_size) : null,
        expected_amount: form.expected_amount.trim() ? Number(form.expected_amount) : null,
        follow_up_date: v(form.follow_up_date),
        ...(form.status ? { status: form.status } : {}),
        notes: v(form.notes),
      });
    },
    onSuccess: () => { closeForm(); qc.invalidateQueries({ queryKey: ['leads'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed'),
  });

  /** Open the full form on an existing lead, every field filled in. */
  const openEdit = (l: Lead) => {
    setErr('');
    setForm({
      full_name: l.full_name ?? '', phone: l.phone ?? '', place: l.place ?? '', district: l.district ?? '',
      category: l.category ?? '', source: l.source ?? '', referred_by_text: l.referred_by_text ?? '',
      lead_type: l.lead_type === 'locker' ? 'locker' : 'ncd',
      interested_scheme: l.interested_scheme ?? '', locker_size: l.locker_size ?? '',
      expected_amount: l.expected_amount != null ? String(Number(l.expected_amount)) : '',
      follow_up_date: l.follow_up_date ? String(l.follow_up_date).slice(0, 10) : '',
      status: l.status ?? '', notes: l.notes ?? '',
    });
    setEditingLead(l);
  };
  /** Close the form. An edit is thrown away so the next "Create" starts blank;
   *  a half-typed NEW lead is kept, as it always was. */
  const closeForm = () => {
    setErr('');
    if (editingLead) setForm(EMPTY_FORM);
    setEditingLead(null);
    setCreating(false);
  };
  /** A config list plus the value already on the lead, so a lead saved before
   *  a value was removed from the list still SHOWS it — a select that cannot
   *  display the current value reads as blank and invites overwriting it. */
  const withCurrent = (list: string[], current: string) => (current && !list.includes(current) ? [current, ...list] : list);

  // Converting a lead now opens the FULL customer form (name pre-filled) rather
  // than creating a bare customer. On save we link the lead to the new customer.
  const [convertLead, setConvertLead] = useState<Lead | null>(null);

  return (
    <div className="w-full">
      {/* Convert = the full customer form, name (and what else we know) pre-filled.
          On save we link the lead to the new customer and land on their profile. */}
      {convertLead && (
        <CustomerWizard
          prefill={{
            full_name: convertLead.full_name,
            phone: convertLead.phone ?? '',
            district: convertLead.district ?? '',
            referred_by_text: convertLead.referred_by_text ?? '',
          }}
          onCreated={async (customerId) => { await api.post(`/api/leads/${convertLead.id}/link-customer`, { customer_id: customerId }); qc.invalidateQueries({ queryKey: ['leads'] }); }}
          onClose={() => setConvertLead(null)}
        />
      )}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold tracking-tight m-0">Leads</h1>
          <p className="text-sm text-text-muted mt-1">Prospective investors you're following up.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Every lead this person can see, plus the App prospects tab, as a
              spreadsheet (owner 2026-09-21). The whole list, not just the tab
              or search on screen — Status is a column, so filter in Excel. */}
          <a href="/api/leads/report.xlsx"
             className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg"
             title="All your leads (every status) and the app prospects, with who created each lead, its branch and the latest follow-up note">
            ↓ Download Excel
          </a>
          {can('leads:create') && !creating && (
            <button onClick={() => { setErr(''); setCreating(true); }} className="text-xs bg-primary text-white rounded px-3 py-1.5 hover:bg-primary-hover">+ Create Lead</button>
          )}
        </div>
      </div>

      {((can('leads:create') && creating) || (can('leads:update') && editingLead)) && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center overflow-y-auto py-8 px-4"
          onClick={closeForm}>
          <div className="bg-surface border border-border rounded-lg shadow-lg w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <h2 className="text-base font-bold m-0">{editingLead ? `Edit lead — ${editingLead.full_name}` : 'New lead'}</h2>
              <button onClick={closeForm} className="text-text-muted hover:text-text text-lg leading-none" aria-label="Close">✕</button>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {/* Name leads (owner 2026-07-23) — it's the required field, and
                  staff fill the form top-left first. */}
              <Field label="Full name" required>
                <input className={`${inp} w-full`} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} autoFocus />
              </Field>
              <Field label="Phone number">
                <input className={`${inp} w-full`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Place">
                <input className={`${inp} w-full`} value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} />
              </Field>
              <Field label="District">
                <input className={`${inp} w-full`} value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
              </Field>
              <Field label="Category">
                <select className={`${inp} w-full`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">Select…</option>
                  {withCurrent(CATEGORIES, form.category).map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Source">
                <select className={`${inp} w-full`} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  <option value="">Select…</option>
                  {withCurrent(SOURCES, form.source).map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Referred by">
                <select className={`${inp} w-full`} value={form.referred_by_text} onChange={(e) => setForm({ ...form, referred_by_text: e.target.value })}>
                  <option value="">Select…</option>
                  {withCurrent(REFERRED_BY, form.referred_by_text).map((r) => <option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Interested in" required>
                <div className="flex gap-4 items-center h-[34px]">
                  {(['ncd', 'locker'] as const).map((t) => (
                    <label key={t} className="inline-flex items-center gap-1.5 text-sm">
                      <input type="radio" name="lead_type" checked={form.lead_type === t}
                        onChange={() => setForm({ ...form, lead_type: t, interested_scheme: '', locker_size: '' })} />
                      {t === 'ncd' ? 'NCD investment' : 'Locker'}
                    </label>
                  ))}
                </div>
              </Field>
              {form.lead_type === 'ncd' ? (
                <Field label="Interested scheme">
                  <select className={`${inp} w-full`} value={form.interested_scheme} onChange={(e) => setForm({ ...form, interested_scheme: e.target.value })}>
                    <option value="">Select…</option>
                    {withCurrent(SCHEMES, form.interested_scheme).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="Locker size">
                  <select className={`${inp} w-full`} value={form.locker_size} onChange={(e) => setForm({ ...form, locker_size: e.target.value })}>
                    <option value="">Select…</option>
                    {LOCKER_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Expected amount (₹)">
                <input className={`${inp} w-full`} type="number" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} />
              </Field>
              <Field label="Follow-up date">
                <input className={`${inp} w-full`} type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} />
              </Field>
              <Field label="Status">
                <select className={`${inp} w-full`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {!editingLead && <option value="">Default (New)</option>}
                  {withCurrent(STATUSES, form.status).map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <textarea className={`${inp} w-full`} rows={3} placeholder="Any notes or discussion details about the customer…"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Field>
              </div>
              {dup.data?.duplicate && (!editingLead || phone !== (editingLead.phone ?? '').trim()) && (
                <div className="sm:col-span-2 text-xs text-warn">
                  ⚠ This phone belongs to existing customer <button className="font-mono underline" onClick={() => nav(`/app/customers/${dup.data!.customer!.id}`)}>{dup.data.customer!.customer_code}</button> ({dup.data.customer!.full_name}){editingLead ? '.' : ' — consider a handover request instead of a new lead.'}
                </div>
              )}
            </div>
            {err && <div className="text-xs text-danger px-5 pb-1">{err}</div>}
            <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-border">
              <button onClick={closeForm} className="text-sm text-text-muted hover:underline px-3 py-1.5">Cancel</button>
              <button disabled={!form.full_name.trim() || create.isPending || update.isPending}
                onClick={() => { setErr(''); if (editingLead) update.mutate(editingLead.id); else create.mutate(); }}
                className="bg-primary hover:bg-primary-hover disabled:opacity-40 text-white rounded px-4 py-1.5 text-sm font-semibold">
                {editingLead ? 'Save changes' : 'Save lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {err && !creating && !editingLead && <div className="text-xs text-danger mb-3">{err}</div>}

      <input
        className="w-full max-w-md px-3 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary mb-4"
        placeholder="Search leads by name, phone, place, district…"
        value={q} onChange={(e) => setQ(e.target.value)}
      />

      {error ? <div className="text-danger">Failed to load leads.</div> : isLoading ? <div className="text-text-muted">Loading…</div> : (() => {
        const columns: Column<Lead>[] = [
          { key: 'full_name', header: 'Name', tdClassName: 'font-medium' },
          { key: 'phone', header: 'Phone', tdClassName: 'text-text-muted', value: (l) => l.phone ?? '', render: (l) => l.phone ?? '—' },
          { key: 'district', header: 'District', value: (l) => l.district ?? '', render: (l) => l.district ?? '—' },
          { key: 'source', header: 'Source', value: (l) => l.source ?? '', render: (l) => l.source ?? '—' },
          { key: 'interest', header: 'Interested in',
            value: (l) => l.lead_type === 'locker' ? `Locker ${l.locker_size ?? ''}` : (l.interested_scheme ?? 'NCD'),
            render: (l) => l.lead_type === 'locker'
              ? <span>Locker{l.locker_size ? <span className="text-text-muted"> · {LOCKER_SIZES.find((s) => s.value === l.locker_size)?.label ?? l.locker_size}</span> : ''}</span>
              : <span>NCD{l.interested_scheme ? <span className="text-text-muted"> · {l.interested_scheme}</span> : ''}</span> },
          { key: 'expected_amount', header: 'Expected', align: 'right', value: (l) => Number(l.expected_amount ?? 0),
            render: (l) => l.expected_amount ? <span className="mono">₹{Number(l.expected_amount).toLocaleString('en-IN')}</span> : '—' },
          { key: 'follow_up_date', header: 'Follow-up', value: (l) => l.follow_up_date ?? '',
            render: (l) => l.follow_up_date ? <span className="mono text-xs">{String(l.follow_up_date).slice(0, 10)}</span> : '—' },
          { key: 'status', header: 'Status',
            render: (l) => <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{l.status}</span> },
          { key: 'actions', header: '', sortable: false, filterable: false, align: 'right', tdClassName: 'whitespace-nowrap',
            render: (l) => {
              return (
                <span className="inline-flex items-center gap-2.5">
                  <button onClick={() => setNotesFor(notesFor === l.id ? null : l.id)} className="text-xs text-primary hover:underline">Notes</button>
                  {can('leads:update') && l.status !== 'Converted' && (
                    <button onClick={() => openEdit(l)} className="text-xs text-primary hover:underline">Edit</button>
                  )}
                  {can('leads:convert') && can('customers:create') && l.status !== 'Converted' && (
                    <button onClick={() => { setErr(''); setConvertLead(l); }}
                      className="text-xs text-primary hover:underline">Convert →</button>
                  )}
                </span>
              );
            } },
        ];
        const ql = q.trim().toLowerCase();
        const matches = (vals: (string | null | undefined)[]) => !ql || vals.some((v) => String(v ?? '').toLowerCase().includes(ql));
        const leadRows = data!.rows.filter((l) => matches([l.full_name, l.phone, l.place, l.district, l.source, l.interested_scheme, l.category]));
        const prospectRows = (prospects.data?.rows ?? []).filter((p) => matches([p.full_name, p.phone, p.district, p.customer_code]));
        // One tab per lead status present (statuses are config-driven), + All,
        // + the dhanamfin app-prospects pool.
        const statuses = [...new Set(leadRows.map((l) => l.status))].sort();
        const leadTabs: TabDef<string>[] = [
          { key: 'all', label: 'All', count: leadRows.length },
          ...statuses.map((s) => ({ key: s, label: s, count: leadRows.filter((l) => l.status === s).length })),
          { key: PROSPECTS_TAB, label: 'App prospects', count: prospectRows.length },
        ];
        const activeTab = leadTabs.some((t) => t.key === tab) ? tab : 'all';

        if (activeTab === PROSPECTS_TAB) {
          const pcols: Column<Prospect>[] = [
            { key: 'customer_code', header: 'Code', tdClassName: 'font-mono text-xs' },
            { key: 'full_name', header: 'Name', tdClassName: 'font-medium' },
            { key: 'phone', header: 'Phone', tdClassName: 'text-text-muted', value: (p) => p.phone ?? '', render: (p) => p.phone ?? '—' },
            { key: 'district', header: 'District', value: (p) => p.district ?? '', render: (p) => p.district ?? '—' },
            { key: 'kyc_status', header: 'KYC', render: (p) => <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{p.kyc_status ?? '—'}</span> },
            { key: 'created_at', header: 'Added', value: (p) => p.created_at, render: (p) => <span className="mono text-xs">{String(p.created_at).slice(0, 10)}</span> },
            { key: 'actions', header: '', sortable: false, filterable: false, align: 'right', tdClassName: 'whitespace-nowrap',
              render: (p) => <button onClick={() => nav(`/app/customers/${p.id}`)} className="text-xs text-primary hover:underline">Enrol →</button> },
          ];
          return (
            <>
              <Tabs tabs={leadTabs} active={activeTab} onChange={setTab} />
              <p className="text-xs text-text-muted mb-3">Dhanamfin app profiles with no NCD investment yet. Open one to enrol them — once they have an application they move to Customers.</p>
              <DataTable columns={pcols} rows={prospectRows} rowKey={(p) => p.id} defaultSort={{ key: 'created_at', dir: 'desc' }} empty="No app prospects." />
            </>
          );
        }

        const shown = activeTab === 'all' ? leadRows : leadRows.filter((l) => l.status === activeTab);
        return (
          <>
            <Tabs tabs={leadTabs} active={activeTab} onChange={setTab} />
            <DataTable
              columns={columns}
              rows={shown}
              rowKey={(l) => l.id}
              empty="No leads in this view."
              renderExpanded={(l) => (notesFor === l.id ? <NotesPanel leadId={l.id} canUpdate={can('leads:update')} /> : null)}
            />
          </>
        );
      })()}
    </div>
  );
}
